import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HammerIcon, SignOutIcon, LockIcon, BriefcaseIcon, ListIcon, CalendarBlankIcon, ShieldCheckIcon, FileTextIcon, DownloadSimpleIcon } from "@phosphor-icons/react";
import { MonthCalendar } from "@/components/MonthCalendar";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const STATUS_STYLE = {
  new: "bg-slate-200 text-slate-800",
  scheduled: "bg-gold-100 text-gold-800",
  "in-progress": "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
};

export default function SubPortal() {
  const [me, setMe] = useState(null); // null=checking, false=logged out, object=logged in
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [doneFiles, setDoneFiles] = useState({});
  const [doneBusy, setDoneBusy] = useState(null);
  const [subView, setSubView] = useState("list");
  const [agreeName, setAgreeName] = useState("");

  const signAgreement = async () => {
    if (!agreeName.trim()) { toast.error("Type your full name to sign"); return; }
    try {
      const res = await api.post("/sub/sign-agreement", { signature_name: agreeName });
      setMe({ ...me, agreement: res.data.agreement });
      toast.success("Agreement signed — thank you!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not sign agreement");
    }
  };

  const markDone = async (j) => {
    setDoneBusy(j.request_id);
    try {
      const files = doneFiles[j.request_id];
      const paths = [];
      if (files) {
        for (const file of files) {
          const fd = new FormData();
          fd.append("file", file);
          const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          paths.push(r.data.path);
        }
      }
      const res = await api.post(`/sub/jobs/${j.request_id}/complete`, { photos: paths });
      setData(res.data);
      toast.success(`Job marked done${paths.length ? ` · ${paths.length} photo(s) uploaded` : ""}`);
    } catch {
      toast.error("Failed to complete job");
    } finally {
      setDoneBusy(null);
    }
  };

  const loadJobs = async () => {
    try {
      const res = await api.get("/sub/jobs");
      setData(res.data);
    } catch { /* noop */ }
  };

  useEffect(() => {
    api.get("/sub/me")
      .then((r) => { setMe(r.data); loadJobs(); })
      .catch(() => setMe(false));
  }, []);

  const login = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/sub/login", { email, password });
      setMe(res.data);
      await loadJobs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const respond = async (j, action) => {
    try {
      const res = await api.post(`/sub/jobs/${j.request_id}/respond`, { action });
      setData(res.data);
      toast.success(action === "accept" ? "Job accepted" : "Job declined");
    } catch {
      toast.error("Failed to update");
    }
  };

  const logout = async () => {
    await api.post("/sub/logout").catch(() => {});
    setMe(false); setData(null); setEmail(""); setPassword("");
  };

  if (me === null) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }

  if (!me) {
    return (
      <div className="min-h-screen bg-navy-900 flex flex-col items-center justify-center px-6">
        <div className="flex items-center gap-2 mb-8">
          <span className="w-9 h-9 rounded-sm bg-gold-600 flex items-center justify-center"><HammerIcon size={20} weight="fill" className="text-white" /></span>
          <span className="font-heading font-extrabold text-lg text-white">MCC Service Corp. — Subcontractor Portal</span>
        </div>
        <form onSubmit={login} className="w-full max-w-sm bg-white rounded-sm p-8" data-testid="sub-login-form">
          <p className="font-heading font-bold text-slate-900 flex items-center gap-2 mb-6"><LockIcon size={18} weight="fill" className="text-gold-600" /> Subcontractor sign in</p>
          <Label className="mb-1 block">Email</Label>
          <Input data-testid="sub-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="bg-white border-slate-300 mb-4" />
          <Label className="mb-1 block">Password</Label>
          <Input data-testid="sub-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="bg-white border-slate-300 mb-6" />
          <button data-testid="sub-login-submit" type="submit" disabled={busy} className="w-full rounded-sm bg-gold-600 hover:bg-gold-700 text-white font-semibold py-2.5 transition-colors active:scale-95 disabled:opacity-50">
            {busy ? "Signing in…" : "Sign In"}
          </button>
          <p className="text-xs text-slate-400 mt-4 text-center">No login yet? Ask MCC Service Corp. to set up your access.</p>
        </form>
      </div>
    );
  }

  const calJobs = (data?.jobs || [])
    .filter((j) => j.scheduled_date)
    .map((j) => ({ id: j.request_id, date: j.scheduled_date, title: `${j.ref} · ${j.customer}`, status: j.status }));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-navy-900 text-white">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-sm bg-gold-600 flex items-center justify-center"><HammerIcon size={18} weight="fill" className="text-white" /></span>
            <span className="font-heading font-extrabold">MCC Service Corp.</span>
          </div>
          <button data-testid="sub-logout-btn" onClick={logout} className="text-sm font-semibold text-slate-300 hover:text-white inline-flex items-center gap-1.5 transition-colors"><SignOutIcon size={16} /> Sign out</button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10" data-testid="sub-dashboard">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold-600 mb-2">Your work</p>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-1">{me.company_name}</h1>
        <p className="text-slate-500 mb-8">{me.contact_name}</p>

        <div data-testid="sub-agreement-card" className={`mb-8 rounded-sm border p-5 ${me.agreement ? "bg-green-50 border-green-200" : "bg-gold-50 border-gold-300"}`}>
          <p className="font-heading font-bold text-slate-900 flex items-center gap-2"><ShieldCheckIcon size={18} weight="fill" className="text-gold-600" /> Independent Subcontractor Agreement &amp; Hold Harmless</p>
          {me.agreement ? (
            <>
              <p data-testid="sub-agreement-signed" className="text-sm text-green-700 font-semibold mt-2">Signed by {me.agreement.signed_name} on {me.agreement.signed_at?.slice(0, 10)}</p>
              <a data-testid="sub-agreement-download" href={`${API}/sub/agreement.pdf`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-700 hover:text-gold-800"><DownloadSimpleIcon size={15} /> Download signed agreement</a>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-600 mt-2">Before taking jobs, please review and sign our agreement. It confirms your independent-contractor status, insurance and tax responsibilities, and that you agree to <b>hold MCC Service Corp. harmless</b> for your work.</p>
              <a data-testid="sub-agreement-read" href={`${API}/subcontractor-agreement/sample.pdf`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-700 hover:text-gold-800"><FileTextIcon size={15} /> Read the full agreement (PDF)</a>
              <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-center">
                <Input data-testid="sub-agreement-name" value={agreeName} onChange={(e) => setAgreeName(e.target.value)} placeholder="Type your full name to sign" className="bg-white border-slate-300 sm:max-w-xs" />
                <button data-testid="sub-agreement-sign-btn" onClick={signAgreement} className="rounded-sm bg-gold-600 hover:bg-gold-700 text-white text-sm font-semibold px-5 py-2 transition-colors active:scale-95">Sign Agreement</button>
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3 mb-8">
          <div className="bg-white border border-slate-200 rounded-sm p-4 text-center"><p className="text-[11px] uppercase tracking-wide text-slate-500">Total</p><p data-testid="sub-total" className="font-heading text-xl font-extrabold text-slate-900">{money(data?.total_paid)}</p></div>
          <div className="bg-green-50 border border-green-200 rounded-sm p-4 text-center"><p className="text-[11px] uppercase tracking-wide text-green-700">Paid</p><p data-testid="sub-settled" className="font-heading text-xl font-extrabold text-green-700">{money(data?.settled)}</p></div>
          <div className="bg-gold-50 border border-gold-200 rounded-sm p-4 text-center"><p className="text-[11px] uppercase tracking-wide text-gold-700">Outstanding</p><p data-testid="sub-outstanding" className="font-heading text-xl font-extrabold text-gold-700">{money(data?.outstanding)}</p></div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <button
            data-testid="sub-view-list"
            onClick={() => setSubView("list")}
            className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-sm transition-colors ${subView === "list" ? "bg-navy-900 text-white" : "text-slate-600 hover:text-slate-900 border border-slate-200"}`}
          >
            <ListIcon size={16} /> List
          </button>
          <button
            data-testid="sub-view-calendar"
            onClick={() => setSubView("calendar")}
            className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-sm transition-colors ${subView === "calendar" ? "bg-navy-900 text-white" : "text-slate-600 hover:text-slate-900 border border-slate-200"}`}
          >
            <CalendarBlankIcon size={16} /> Calendar
          </button>
        </div>

        {subView === "calendar" ? (
          <MonthCalendar jobs={calJobs} onJobClick={() => setSubView("list")} />
        ) : (
        <div className="bg-white border border-slate-200 rounded-sm">
          {!data || data.jobs.length === 0 ? (
            <p className="p-8 text-center text-slate-400 flex flex-col items-center gap-2"><BriefcaseIcon size={28} weight="duotone" /> No jobs assigned to you yet.</p>
          ) : (
            data.jobs.map((j, i) => (
              <div key={i} data-testid={`sub-portal-job-${j.ref}`} className="border-b border-slate-100 last:border-0 px-5 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-semibold text-gold-700">{j.ref}</p>
                    <p className="text-sm text-slate-700">{j.customer}</p>
                    <p className="text-xs text-slate-500">{j.scheduled_date || "Date TBD"}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase ${STATUS_STYLE[j.status] || "bg-slate-200"}`}>{j.status}</span>
                    <span className="font-semibold text-slate-900">{money(j.pay_amount)}</span>
                    <span className={`px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase ${j.paid ? "bg-green-600 text-white" : "bg-gold-100 text-gold-800"}`}>{j.paid ? "Paid" : "Unpaid"}</span>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  {j.response === "pending" || !j.response ? (
                    <>
                      <span className="text-xs text-slate-500 mr-1">Respond to this assignment:</span>
                      <button data-testid={`accept-job-${j.ref}`} onClick={() => respond(j, "accept")} className="rounded-sm bg-green-600 hover:bg-green-700 text-white text-[11px] font-bold uppercase px-3 py-1 transition-colors active:scale-95">Accept</button>
                      <button data-testid={`decline-job-${j.ref}`} onClick={() => respond(j, "decline")} className="rounded-sm border border-slate-300 text-slate-600 hover:border-red-500 hover:text-red-600 text-[11px] font-bold uppercase px-3 py-1 transition-colors active:scale-95">Decline</button>
                    </>
                  ) : (
                    <span data-testid={`job-response-${j.ref}`} className={`px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase ${j.response === "accepted" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>{j.response === "accepted" ? "You accepted" : "You declined"}</span>
                  )}
                </div>
                {j.response === "accepted" && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {j.rejections && j.rejections.length > 0 && (
                      <div data-testid={`rejections-${j.ref}`} className="w-full rounded-sm bg-red-50 border border-red-200 p-2 mb-1">
                        <p className="text-[11px] font-bold uppercase text-red-700 mb-1">Photos to redo</p>
                        {j.rejections.map((rj, k) => (
                          <p key={k} className="text-xs text-red-700">• {rj.reason || "Please re-upload a clearer photo."}</p>
                        ))}
                      </div>
                    )}
                    {j.sub_done ? (
                      <span data-testid={`job-done-${j.ref}`} className="px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase bg-blue-100 text-blue-800">Completed ✓</span>
                    ) : (
                      <>
                        <span className="text-xs text-slate-500 mr-1">Upload completed-work photos &amp; finish:</span>
                        <input data-testid={`done-files-${j.ref}`} type="file" multiple accept="image/*" onChange={(e) => setDoneFiles((f) => ({ ...f, [j.request_id]: e.target.files }))} className="text-xs text-slate-600 max-w-[190px]" />
                        <button data-testid={`mark-done-${j.ref}`} onClick={() => markDone(j)} disabled={doneBusy === j.request_id} className="rounded-sm bg-navy-900 hover:bg-navy-800 text-white text-[11px] font-bold uppercase px-3 py-1 transition-colors active:scale-95 disabled:opacity-50">{doneBusy === j.request_id ? "Saving…" : "Mark job done"}</button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        )}
      </main>
    </div>
  );
}
